import { useEffect, useState } from 'react';

export interface Color {
  r: number;
  g: number;
  b: number;
}

const imageBlockSize = 5;
const defaultColor: Color = { r: 0, g: 0, b: 0 };

function calculateColor(el: HTMLImageElement): Color {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (context === null || el === null) {
    return defaultColor;
  }

  canvas.width = el.naturalWidth || el.offsetWidth || el.width;
  canvas.height = el.naturalHeight || el.offsetHeight || el.height;

  context.drawImage(el, 0, 0);

  let data: ImageData;
  try {
    data = context.getImageData(0, 0, canvas.width, canvas.height);
  } catch (e) {
    return defaultColor;
  }

  let count = 0;
  let i = -4;
  let rgb: Color = { r: 0, g: 0, b: 0 };

  while ((i += imageBlockSize * 4) < data.data.length) {
    ++count;
    rgb.r += data.data[i];
    rgb.g += data.data[i + 1];
    rgb.b += data.data[i + 2];
  }

  rgb.r = ~~(rgb.r / count);
  rgb.g = ~~(rgb.g / count);
  rgb.b = ~~(rgb.b / count);

  return rgb;
}

export default function useImageColor(imageUrl?: string): Color {
  const [color, setColor] = useState<Color>(defaultColor);
  const [imageIsReady, setImageIsReady] = useState(false);
  useEffect(() => {
    if (!imageUrl) {
      return;
    }

    if (imageIsReady) {
      setImageIsReady(false);
    }

    let calculationActive = true;

    const el = new Image();
    el.src = imageUrl;
    el.crossOrigin = 'anonymous';
    el.addEventListener('load', () => {
      if (!calculationActive) {
        return;
      }

      setColor(calculateColor(el));
    });

    return () => {
      calculationActive = false;
    };
  }, [imageUrl, imageIsReady]);

  return color;
}
